import React, { useState, useEffect, useMemo, useCallback, memo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ShoppingCart, Search, User, LogOut, Package, Plus, Minus, 
  CreditCard, Tag, X, Check, Timer, Menu, Home, Percent,
  ChevronRight, Gift, AlertCircle, Sparkles, MapPin,
  Grid3X3, List, Eye, ChevronLeft, Info
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { toast } from 'sonner';

// SmartCaddy Logo Component
const CaddyLogo = ({ size = 'default' }) => {
  const sizes = {
    small: { icon: 'w-8 h-8', text: 'text-lg' },
    default: { icon: 'w-10 h-10', text: 'text-xl' },
  };
  const s = sizes[size];
  
  return (
    <div className="flex items-center gap-2">
      <div className={`${s.icon} rounded-xl bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center shadow-lg shadow-teal-500/30`}>
        <ShoppingCart className="w-1/2 h-1/2 text-white" />
      </div>
      <span className={`font-caddy ${s.text} lowercase text-teal-600`}>caddy</span>
    </div>
  );
};

// Product Detail View Component - Full height for better visibility
const ProductDetailView = memo(({ product, offers, onClose, isCartItem = false }) => {
  if (!product) return null;
  
  const applicableOffers = offers.filter(o => 
    o.applicable_products?.includes(product.id || product.product_id) || 
    o.applicable_categories?.includes(product.category) ||
    (!o.applicable_products && !o.applicable_categories)
  );
  
  return (
    <div className="h-full flex flex-col bg-white rounded-2xl overflow-hidden border border-slate-200">
      {/* Header */}
      <div className="p-3 flex items-center gap-3 border-b border-slate-100 bg-gradient-to-r from-teal-50 to-cyan-50 flex-shrink-0">
        <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8 text-slate-600 hover:bg-white/50">
          <ChevronLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-slate-800 truncate">{product.name}</h3>
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className="border-teal-300 text-teal-700 text-xs h-5">{product.category}</Badge>
            {isCartItem && <Badge className="bg-teal-500 text-white text-xs h-5">In Caddy</Badge>}
          </div>
        </div>
      </div>
      
      {/* Content - Side by side layout */}
      <div className="flex-1 flex flex-col md:flex-row overflow-auto">
        {/* Product Image */}
        <div className="w-full md:w-2/5 bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4 flex-shrink-0" style={{ minHeight: '150px' }}>
          {product.image_url ? (
            <img src={product.image_url} alt={product.name} className="max-h-40 max-w-full object-contain rounded-lg" />
          ) : (
            <Package className="w-20 h-20 text-slate-300" />
          )}
        </div>
        
        {/* Product Info */}
        <div className="flex-1 p-4 flex flex-col">
          {/* Price and Stock Row */}
          <div className="flex items-center justify-between mb-3">
            <span className="text-3xl font-bold text-teal-600">₹{product.price}</span>
            <span className={`text-xs px-2 py-1 rounded-full ${
              (product.stock_quantity || 0) > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
            }`}>
              {(product.stock_quantity || 0) > 0 ? `${product.stock_quantity} in stock` : 'Out of stock'}
            </span>
          </div>
          
          {/* Location Info - Prominent display */}
          {(product.aisle || product.shelf) && (
            <div className="flex items-center gap-2 mb-3 p-2 bg-amber-50 rounded-lg border border-amber-200">
              <MapPin className="w-4 h-4 text-amber-600" />
              <span className="text-sm text-amber-800 font-medium">
                {product.aisle && `Aisle ${product.aisle}`}
                {product.aisle && product.shelf && ' • '}
                {product.shelf && `Shelf ${product.shelf}`}
              </span>
            </div>
          )}
          
          {/* Quantity in cart */}
          {isCartItem && product.quantity && (
            <div className="flex items-center gap-2 mb-3 p-2 bg-teal-50 rounded-lg">
              <ShoppingCart className="w-4 h-4 text-teal-600" />
              <span className="text-sm text-teal-700">Qty: <strong>{product.quantity}</strong></span>
              <span className="text-sm text-teal-600 ml-auto font-semibold">₹{(product.price * product.quantity).toFixed(2)}</span>
            </div>
          )}
          
          {/* Description */}
          {product.description && (
            <p className="text-slate-600 text-sm mb-3">{product.description}</p>
          )}
          
          {/* Applicable Offers */}
          {applicableOffers.length > 0 && (
            <div className="mt-auto pt-3 border-t border-slate-100">
              <p className="text-xs font-semibold text-orange-600 mb-2 flex items-center gap-1">
                <Tag className="w-3 h-3" /> {applicableOffers.length} Offer{applicableOffers.length > 1 ? 's' : ''} Available
              </p>
              <div className="space-y-1.5">
                {applicableOffers.slice(0, 3).map(offer => (
                  <div key={offer.id} className="flex items-center justify-between p-2 bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg">
                    <span className="text-xs text-slate-700 truncate flex-1">{offer.title}</span>
                    <Badge className="bg-orange-500 text-white ml-2 text-xs">
                      {offer.discount_type === 'percentage' ? `${offer.discount_value}%` : `₹${offer.discount_value}`}
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});
ProductDetailView.displayName = 'ProductDetailView';

// Cart Panel Component
const CartPanel = memo(({ 
  cartItems, subtotal, tax, total, offers, onCheckout, disabled, onItemClick 
}) => (
  <div className="flex flex-col h-full">
    <ScrollArea className="flex-1">
      {cartItems.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 text-slate-400">
          <ShoppingCart className="w-12 h-12 mb-2 opacity-30" />
          <p className="font-medium text-sm">Your caddy is empty</p>
          <p className="text-xs mt-1">Scan items to add them</p>
        </div>
      ) : (
        <div className="space-y-2 p-2">
          {cartItems.map((item) => (
            <div 
              key={item.product_id} 
              onClick={() => onItemClick && onItemClick(item)}
              className="flex items-center gap-2 p-2 bg-slate-50 rounded-xl hover:bg-teal-50 transition-colors cursor-pointer group"
            >
              <div className="w-12 h-12 rounded-lg overflow-hidden bg-white border border-slate-200 flex-shrink-0">
                {item.image_url ? (
                  <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Package className="w-5 h-5 text-slate-300" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-xs truncate text-slate-800">{item.name}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-xs text-slate-500">₹{item.price}</span>
                  <Badge className="text-xs h-4 bg-teal-100 text-teal-700 border-0">×{item.quantity}</Badge>
                </div>
              </div>
              <div className="text-right">
                <p className="font-semibold text-sm text-slate-800">₹{(item.price * item.quantity).toFixed(2)}</p>
                <Eye className="w-3 h-3 text-slate-400 group-hover:text-teal-500 ml-auto" />
              </div>
            </div>
          ))}
        </div>
      )}
    </ScrollArea>

    {offers.length > 0 && cartItems.length > 0 && (
      <div className="border-t border-slate-100 p-2 bg-gradient-to-r from-teal-50 to-cyan-50">
        <div className="flex items-center gap-2 text-teal-700 text-xs">
          <Gift className="w-3 h-3" />
          <span className="font-medium">Offers Applied</span>
        </div>
        {offers.slice(0, 1).map(o => (
          <div key={o.id} className="text-xs text-teal-600 mt-0.5 flex items-center gap-1">
            <Check className="w-3 h-3" /> {o.title}
          </div>
        ))}
      </div>
    )}

    <div className="border-t border-slate-200 p-3 space-y-1.5 bg-white">
      <div className="flex justify-between text-xs">
        <span className="text-slate-500">Subtotal</span>
        <span className="text-slate-700">₹{subtotal.toFixed(2)}</span>
      </div>
      <div className="flex justify-between text-xs">
        <span className="text-slate-500">Tax</span>
        <span className="text-slate-700">₹{tax.toFixed(2)}</span>
      </div>
      <div className="flex justify-between font-bold text-base pt-1.5 border-t border-slate-100">
        <span className="text-slate-800">Total</span>
        <span className="text-teal-600">₹{total.toFixed(2)}</span>
      </div>

      <Button
        data-testid="checkout-btn"
        onClick={onCheckout}
        disabled={disabled || cartItems.length === 0}
        className="w-full mt-2 h-10 text-sm font-semibold bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white shadow-lg shadow-teal-500/25"
      >
        <CreditCard className="w-4 h-4 mr-2" />
        Checkout
      </Button>
    </div>
  </div>
));
CartPanel.displayName = 'CartPanel';

// Product Card for List View - Now shows location info
const ProductCardList = memo(({ product, onSelect, isSelected }) => {
  const inStock = product.stock_quantity > 0;
  const hasLocation = product.aisle || product.shelf;
  
  return (
    <div 
      onClick={() => inStock && onSelect(product)}
      className={`flex items-center gap-2 p-2 rounded-xl border transition-all cursor-pointer ${
        isSelected 
          ? 'bg-teal-50 border-teal-300 shadow-sm' 
          : inStock 
            ? 'bg-white border-slate-100 hover:border-teal-200 hover:shadow-sm' 
            : 'bg-slate-50 border-slate-100 opacity-60 cursor-not-allowed'
      }`}
    >
      <div className="w-10 h-10 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0">
        {product.image_url ? (
          <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Package className="w-4 h-4 text-slate-300" />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-slate-800 text-xs truncate">{product.name}</p>
        <div className="flex items-center gap-2 text-xs text-slate-400">
          <span>{product.category}</span>
          {hasLocation && (
            <>
              <span>•</span>
              <span className="flex items-center gap-0.5 text-amber-600">
                <MapPin className="w-3 h-3" />
                {product.aisle && `A${product.aisle}`}
                {product.aisle && product.shelf && '/'}
                {product.shelf && `S${product.shelf}`}
              </span>
            </>
          )}
        </div>
      </div>
      <div className="text-right flex-shrink-0">
        <p className="font-bold text-teal-600 text-sm">₹{product.price}</p>
      </div>
    </div>
  );
});
ProductCardList.displayName = 'ProductCardList';

// Product Card for Grid View - Now shows location info
const ProductCardGrid = memo(({ product, onSelect, isSelected }) => {
  const inStock = product.stock_quantity > 0;
  const hasLocation = product.aisle || product.shelf;
  
  return (
    <div 
      onClick={() => inStock && onSelect(product)}
      className={`flex flex-col rounded-xl border transition-all cursor-pointer overflow-hidden ${
        isSelected 
          ? 'bg-teal-50 border-teal-300 shadow-md' 
          : inStock 
            ? 'bg-white border-slate-100 hover:border-teal-200 hover:shadow-md' 
            : 'bg-slate-50 border-slate-100 opacity-60 cursor-not-allowed'
      }`}
    >
      <div className="aspect-square bg-slate-50 flex items-center justify-center p-2 relative">
        {product.image_url ? (
          <img src={product.image_url} alt={product.name} className="max-h-full max-w-full object-contain" />
        ) : (
          <Package className="w-8 h-8 text-slate-300" />
        )}
        {/* Location badge overlay */}
        {hasLocation && (
          <div className="absolute bottom-1 left-1 bg-amber-500 text-white text-xs px-1.5 py-0.5 rounded flex items-center gap-0.5">
            <MapPin className="w-2.5 h-2.5" />
            {product.aisle && `A${product.aisle}`}
            {product.shelf && `S${product.shelf}`}
          </div>
        )}
      </div>
      <div className="p-2 border-t border-slate-100">
        <p className="font-medium text-slate-800 text-xs truncate">{product.name}</p>
        <div className="flex items-center justify-between mt-1">
          <p className="font-bold text-teal-600 text-sm">₹{product.price}</p>
          {!inStock && <span className="text-xs text-red-500">Out</span>}
        </div>
      </div>
    </div>
  );
});
ProductCardGrid.displayName = 'ProductCardGrid';

// Last Scanned Notification - Compact
const LastScannedNotification = memo(({ item }) => (
  <motion.div
    initial={{ opacity: 0, y: -10, scale: 0.95 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    exit={{ opacity: 0, y: -10, scale: 0.95 }}
    className={`p-3 rounded-xl border-2 ${
      item.action === 'ADD' 
        ? 'bg-emerald-50 border-emerald-300' 
        : 'bg-red-50 border-red-300'
    }`}
  >
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
        item.action === 'ADD' ? 'bg-emerald-500' : 'bg-red-500'
      }`}>
        {item.action === 'ADD' ? <Plus className="w-5 h-5 text-white" /> : <Minus className="w-5 h-5 text-white" />}
      </div>
      
      <div className="flex-1">
        <p className={`text-xs font-medium ${item.action === 'ADD' ? 'text-emerald-700' : 'text-red-700'}`}>
          {item.action === 'ADD' ? 'ITEM ADDED' : 'ITEM REMOVED'}
        </p>
        <p className="text-slate-800 font-bold text-sm">{item.name}</p>
      </div>
      
      <div className="text-right">
        <p className="font-semibold text-slate-700">₹{item.price}</p>
        <Badge className="bg-slate-200 text-slate-700 text-xs">×{item.quantity}</Badge>
      </div>
    </div>
  </motion.div>
));
LastScannedNotification.displayName = 'LastScannedNotification';

// Offer Card Component
const OfferCard = memo(({ offer }) => (
  <div className="flex items-center gap-2 p-2 bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg border border-orange-100 hover:shadow-sm transition-all cursor-pointer">
    <div className="w-8 h-8 rounded-lg bg-orange-500 flex items-center justify-center flex-shrink-0">
      <Percent className="w-4 h-4 text-white" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="font-medium text-slate-800 text-xs truncate">{offer.title}</p>
      <p className="text-xs text-slate-500 truncate">{offer.description}</p>
    </div>
    <Badge className="bg-orange-500 text-white text-xs flex-shrink-0">
      {offer.discount_type === 'percentage' ? `${offer.discount_value}%` : `₹${offer.discount_value}`}
    </Badge>
  </div>
));
OfferCard.displayName = 'OfferCard';

// Session Timer Component - Only shows when expiring soon
const SessionTimer = memo(({ timeRemaining, isInactive }) => {
  // Only show if time is running low (under 3 minutes) AND user has been inactive
  const shouldShow = timeRemaining !== null && timeRemaining < 180 && isInactive;
  
  if (!shouldShow) return null;
  
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs ${
        timeRemaining < 60 ? 'bg-red-100 text-red-700 animate-pulse' : 'bg-amber-100 text-amber-700'
      }`}
    >
      <Timer className="w-3 h-3" />
      <span className="font-mono">{Math.floor(timeRemaining/60)}:{(timeRemaining%60).toString().padStart(2,'0')}</span>
      <span className="text-xs opacity-75">left</span>
    </motion.div>
  );
});
SessionTimer.displayName = 'SessionTimer';

const ShoppingDashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const cartIdParam = searchParams.get('cart');
  
  const { user, logout, api, isAuthenticated } = useAuth();
  const { 
    session, cartItems, subtotal, tax, total, lastScannedItem,
    hasActiveSession, itemCount, createSession, endSession, checkout,
    refreshSession, loading: cartLoading, timeoutAt,
    createPaymentOrder, verifyPayment
  } = useCart();

  const [products, setProducts] = useState([]);
  const [offers, setOffers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [offerSearchQuery, setOfferSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [categories, setCategories] = useState(['all']);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showCart, setShowCart] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [sessionStartAttempted, setSessionStartAttempted] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isCartItemSelected, setIsCartItemSelected] = useState(false);
  const [offerTab, setOfferTab] = useState('all');
  const [viewMode, setViewMode] = useState('list');
  
  // Track user activity for smart timer display
  const [isInactive, setIsInactive] = useState(false);
  const lastActivityRef = useRef(Date.now());
  const INACTIVITY_THRESHOLD = 60000; // 1 minute of inactivity

  // Track user activity
  useEffect(() => {
    const updateActivity = () => {
      lastActivityRef.current = Date.now();
      setIsInactive(false);
    };
    
    const checkInactivity = () => {
      const timeSinceActivity = Date.now() - lastActivityRef.current;
      if (timeSinceActivity > INACTIVITY_THRESHOLD) {
        setIsInactive(true);
      }
    };
    
    // Listen for user activity
    window.addEventListener('mousemove', updateActivity);
    window.addEventListener('keydown', updateActivity);
    window.addEventListener('touchstart', updateActivity);
    window.addEventListener('scroll', updateActivity);
    
    // Check inactivity periodically
    const inactivityInterval = setInterval(checkInactivity, 10000);
    
    return () => {
      window.removeEventListener('mousemove', updateActivity);
      window.removeEventListener('keydown', updateActivity);
      window.removeEventListener('touchstart', updateActivity);
      window.removeEventListener('scroll', updateActivity);
      clearInterval(inactivityInterval);
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated) { navigate('/login'); return; }
    loadData();
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated || cartLoading || sessionStartAttempted) return;
    
    if (hasActiveSession) {
      setSessionStartAttempted(true);
      return;
    }
    
    if (cartIdParam && !hasActiveSession) {
      setSessionStartAttempted(true);
      handleCreateSession(cartIdParam);
    }
  }, [cartIdParam, hasActiveSession, cartLoading, isAuthenticated, sessionStartAttempted]);

  useEffect(() => {
    if (!timeoutAt) { setTimeRemaining(null); return; }
    const update = () => {
      const diff = Math.max(0, Math.floor((new Date(timeoutAt) - new Date()) / 1000));
      setTimeRemaining(diff);
      if (diff === 0) { toast.error('Session expired'); refreshSession(); }
    };
    update();
    const i = setInterval(update, 1000);
    return () => clearInterval(i);
  }, [timeoutAt, refreshSession]);

  const loadData = async () => {
    try {
      const [p, o, c] = await Promise.all([
        api.get('/products'), 
        api.get('/offers'), 
        api.get('/categories').catch(() => ({ data: { categories: [] } }))
      ]);
      setProducts(p.data || []);
      setOffers(o.data || []);
      setCategories(['all', ...(c.data?.categories || [])]);
    } catch (e) { console.error('Load error:', e); }
  };

  const handleCreateSession = async (cartId) => {
    try {
      await createSession(cartId);
      toast.success('Shopping session started!');
    } catch (e) {
      const msg = e.response?.data?.detail || 'Failed to start session';
      toast.error(msg);
      if (!msg.includes('in use')) navigate('/');
    }
  };

  const handleCheckout = () => {
    if (cartItems.length === 0) { toast.error('Caddy is empty'); return; }
    setShowCheckout(true);
  };

  // Load Razorpay script dynamically
  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const processPayment = async () => {
    setCheckoutLoading(true);
    try {
      // First create a Razorpay order
      const orderData = await createPaymentOrder();
      
      // Load Razorpay script
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        throw new Error('Failed to load payment gateway');
      }

      // Configure Razorpay options
      const options = {
        key: orderData.razorpay_key_id,
        amount: orderData.amount,
        currency: orderData.currency,
        name: 'SmartCaddy',
        description: 'Shopping Cart Payment',
        order_id: orderData.razorpay_order_id,
        handler: async (response) => {
          try {
            // Verify payment on backend
            await verifyPayment(
              response.razorpay_order_id,
              response.razorpay_payment_id,
              response.razorpay_signature
            );
            setShowCheckout(false);
            toast.success('Payment successful! Thank you for shopping.');
            navigate('/orders');
          } catch (err) {
            toast.error('Payment verification failed. Please contact support.');
          }
        },
        prefill: {
          email: orderData.user_email || user?.email || '',
          contact: orderData.user_phone || user?.phone || ''
        },
        theme: {
          color: '#14B8A6' // Teal color to match theme
        },
        modal: {
          ondismiss: () => {
            setCheckoutLoading(false);
            toast.info('Payment cancelled');
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
      
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to initiate payment');
      setCheckoutLoading(false);
    }
  };

  // Test mode payment (simulated)
  const processTestPayment = async () => {
    setCheckoutLoading(true);
    try {
      await checkout();
      setShowCheckout(false);
      toast.success('Test payment successful! Thank you for shopping.');
      navigate('/orders');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Checkout failed');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleEndSession = async () => {
    try {
      await endSession();
      toast.info('Shopping session ended. Caddy is now available.');
      navigate('/');
    } catch (e) {
      toast.warning('Session ended locally. Caddy may need manual release.');
      navigate('/');
    }
  };

  const handleLogout = async () => { await logout(); navigate('/'); };

  const handleProductSelect = useCallback((product) => {
    setSelectedProduct(product);
    setIsCartItemSelected(false);
  }, []);

  const handleCartItemSelect = useCallback((item) => {
    setSelectedProduct(item);
    setIsCartItemSelected(true);
  }, []);

  const filteredProducts = useMemo(() => {
    return products.filter(p => {
      const matchesSearch = !searchQuery || 
        p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.description?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = selectedCategory === 'all' || p.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [products, searchQuery, selectedCategory]);

  const filteredOffers = useMemo(() => {
    let filtered = offers.filter(o => o.is_active !== false);
    
    if (offerSearchQuery) {
      filtered = filtered.filter(o => 
        o.title?.toLowerCase().includes(offerSearchQuery.toLowerCase()) ||
        o.description?.toLowerCase().includes(offerSearchQuery.toLowerCase())
      );
    }
    
    if (offerTab === 'applicable') {
      filtered = filtered.filter(o => !o.min_cart_value || total >= o.min_cart_value);
    } else if (offerTab === 'percentage') {
      filtered = filtered.filter(o => o.discount_type === 'percentage');
    } else if (offerTab === 'flat') {
      filtered = filtered.filter(o => o.discount_type === 'fixed');
    }
    
    return filtered;
  }, [offers, offerSearchQuery, offerTab, total]);

  const applicableOffers = useMemo(() => 
    offers.filter(o => o.is_active !== false && (!o.min_cart_value || total >= o.min_cart_value)).slice(0, 3)
  , [offers, total]);

  const categoryCounts = useMemo(() => {
    const counts = { all: products.length };
    products.forEach(p => { counts[p.category] = (counts[p.category] || 0) + 1; });
    return counts;
  }, [products]);

  if (cartLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-500">Loading session...</p>
        </div>
      </div>
    );
  }

  if (!hasActiveSession) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-teal-50 flex flex-col items-center justify-center p-4">
        <ShoppingCart className="w-20 h-20 mb-6 text-slate-300" />
        <h1 className="text-2xl font-bold text-slate-800 mb-2">No Active Session</h1>
        <p className="text-slate-500 mb-6 text-center max-w-md">
          Scan a caddy QR code or enter a cart code to start shopping
        </p>
        <div className="flex gap-3">
          <Button onClick={() => navigate('/')} size="lg" className="bg-teal-500 hover:bg-teal-600 text-white">
            <Home className="w-5 h-5 mr-2" /> Go Home
          </Button>
          {isAuthenticated && (
            <Button variant="outline" onClick={() => navigate('/dashboard')} size="lg" className="border-teal-300 text-teal-700 hover:bg-teal-50">
              <User className="w-5 h-5 mr-2" /> Dashboard
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-slate-200">
        <div className="flex items-center justify-between px-3 py-2 max-w-7xl mx-auto">
          <CaddyLogo size="small" />
          
          <div className="flex items-center gap-2">
            {/* Smart Timer - Only shows when expiring and inactive */}
            <AnimatePresence>
              <SessionTimer timeRemaining={timeRemaining} isInactive={isInactive} />
            </AnimatePresence>
            
            <Badge variant="outline" className="border-teal-300 text-teal-700 hidden sm:flex text-xs">
              {session?.cart_code}
            </Badge>
          </div>

          <div className="hidden md:flex items-center gap-1">
            <Button variant="ghost" size="sm" className="text-slate-600 hover:text-slate-800 hover:bg-slate-100 h-8 text-xs" onClick={() => navigate('/dashboard')}>
              <User className="w-3 h-3 mr-1" /> Dashboard
            </Button>
            <Button data-testid="end-session-btn" variant="ghost" size="sm" className="text-orange-600 hover:text-orange-700 hover:bg-orange-50 h-8 text-xs" onClick={handleEndSession}>
              <X className="w-3 h-3 mr-1" /> End
            </Button>
            <Button variant="ghost" size="icon" className="text-slate-600 hover:text-slate-800 hover:bg-slate-100 h-8 w-8" onClick={handleLogout}>
              <LogOut className="w-3 h-3" />
            </Button>
          </div>

          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon" className="text-slate-600 h-8 w-8"><Menu className="w-4 h-4" /></Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-64 bg-white border-slate-200">
              <SheetHeader><SheetTitle className="text-slate-800">Menu</SheetTitle></SheetHeader>
              <div className="flex flex-col gap-2 mt-4">
                {timeRemaining !== null && timeRemaining < 180 && (
                  <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${timeRemaining < 60 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                    <Timer className="w-4 h-4" /> {Math.floor(timeRemaining/60)}:{(timeRemaining%60).toString().padStart(2,'0')} left
                  </div>
                )}
                <Button variant="ghost" className="justify-start text-slate-600 hover:bg-slate-100" onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }}>
                  <User className="w-4 h-4 mr-2" /> Dashboard
                </Button>
                <Button variant="ghost" className="justify-start text-orange-600 hover:bg-orange-50" onClick={handleEndSession}>
                  <X className="w-4 h-4 mr-2" /> End Session
                </Button>
                <Button variant="ghost" className="justify-start text-red-600 hover:bg-red-50" onClick={handleLogout}>
                  <LogOut className="w-4 h-4 mr-2" /> Logout
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </header>

      {/* Main Content - 3 Panel Layout */}
      <main className="flex-1 flex flex-col lg:flex-row max-w-7xl mx-auto w-full overflow-hidden">
        {/* Left Panel - Offers */}
        <aside className="hidden lg:flex flex-col w-64 border-r border-slate-200 bg-white flex-shrink-0">
          <div className="p-3 border-b border-slate-100">
            <h2 className="font-semibold flex items-center gap-2 text-slate-800 text-sm mb-2">
              <Tag className="w-4 h-4 text-orange-500" /> Offers & Deals
            </h2>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400" />
              <Input
                placeholder="Search offers..."
                value={offerSearchQuery}
                onChange={e => setOfferSearchQuery(e.target.value)}
                className="pl-8 bg-slate-50 border-slate-200 h-8 text-xs"
              />
            </div>
          </div>

          <Tabs value={offerTab} onValueChange={setOfferTab} className="flex-1 flex flex-col overflow-hidden">
            <TabsList className="grid grid-cols-4 bg-slate-100 mx-3 mt-2 h-7">
              <TabsTrigger value="all" className="text-xs data-[state=active]:bg-teal-500 data-[state=active]:text-white">All</TabsTrigger>
              <TabsTrigger value="applicable" className="text-xs data-[state=active]:bg-teal-500 data-[state=active]:text-white">You</TabsTrigger>
              <TabsTrigger value="percentage" className="text-xs data-[state=active]:bg-teal-500 data-[state=active]:text-white">%</TabsTrigger>
              <TabsTrigger value="flat" className="text-xs data-[state=active]:bg-teal-500 data-[state=active]:text-white">₹</TabsTrigger>
            </TabsList>

            <ScrollArea className="flex-1 p-3">
              <div className="space-y-2">
                {filteredOffers.length === 0 ? (
                  <div className="text-center py-6 text-slate-400">
                    <Gift className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-xs">No offers found</p>
                  </div>
                ) : (
                  filteredOffers.map(offer => (
                    <OfferCard key={offer.id} offer={offer} />
                  ))
                )}
              </div>
            </ScrollArea>
          </Tabs>
        </aside>

        {/* Center Panel - Product Detail (55%) + Browse Products (45%) */}
        <section className="flex-1 flex flex-col min-h-0 overflow-hidden">
          {/* Top: Notification / Product Detail / Caddy Summary - 55% height */}
          <div className="border-b border-slate-200 bg-white flex-shrink-0" style={{ flex: '0 0 55%', minHeight: '250px' }}>
            <AnimatePresence mode="wait">
              {lastScannedItem ? (
                <div className="p-4 h-full">
                  <LastScannedNotification item={lastScannedItem} />
                </div>
              ) : selectedProduct ? (
                <motion.div
                  key="product-detail"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="h-full"
                >
                  <ProductDetailView 
                    product={selectedProduct} 
                    offers={offers}
                    onClose={() => { setSelectedProduct(null); setIsCartItemSelected(false); }}
                    isCartItem={isCartItemSelected}
                  />
                </motion.div>
              ) : (
                <motion.div
                  key="cart-summary"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="p-4 h-full flex flex-col"
                >
                  <Card className="flex-1 bg-gradient-to-r from-teal-500 to-cyan-500 border-0 text-white overflow-hidden">
                    <CardContent className="p-5 h-full flex flex-col justify-between">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-14 h-14 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center">
                            <ShoppingCart className="w-7 h-7" />
                          </div>
                          <div>
                            <h3 className="text-xl font-bold">Your Caddy</h3>
                            <p className="text-white/80">{itemCount} items</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-3xl font-bold">₹{total.toFixed(2)}</p>
                          {applicableOffers.length > 0 && (
                            <p className="text-white/80 text-sm flex items-center justify-end gap-1">
                              <Gift className="w-3 h-3" /> {applicableOffers.length} offers available
                            </p>
                          )}
                        </div>
                      </div>
                      
                      <div className="mt-4">
                        {cartItems.length > 0 ? (
                          <Button 
                            onClick={handleCheckout} 
                            className="w-full h-12 bg-white text-teal-600 hover:bg-white/90 font-semibold text-base"
                          >
                            <CreditCard className="w-5 h-5 mr-2" /> Proceed to Checkout
                          </Button>
                        ) : (
                          <p className="text-center text-white/70">
                            Scan products to add them to your caddy
                          </p>
                        )}
                      </div>
                      
                      <p className="text-center text-white/50 text-xs mt-3">
                        Select a product below to view details and offers
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Bottom: Browse Products Section - 45% height */}
          <div className="flex-1 flex flex-col min-h-0 bg-slate-50 overflow-hidden" style={{ flex: '0 0 45%' }}>
            {/* Search & View Toggle - Compact */}
            <div className="p-2 border-b border-slate-200 space-y-1.5 bg-white flex-shrink-0">
              <div className="flex items-center justify-between gap-2">
                <h3 className="font-semibold text-slate-800 flex items-center gap-1 text-xs">
                  <Package className="w-3 h-3 text-teal-600" /> Browse Products
                </h3>
                <div className="flex items-center gap-1">
                  <span className="text-xs text-slate-400">{filteredProducts.length}</span>
                  <Button
                    variant={viewMode === 'list' ? 'default' : 'ghost'}
                    size="icon"
                    className={`h-6 w-6 ${viewMode === 'list' ? 'bg-teal-500 text-white' : 'text-slate-500'}`}
                    onClick={() => setViewMode('list')}
                  >
                    <List className="w-3 h-3" />
                  </Button>
                  <Button
                    variant={viewMode === 'grid' ? 'default' : 'ghost'}
                    size="icon"
                    className={`h-6 w-6 ${viewMode === 'grid' ? 'bg-teal-500 text-white' : 'text-slate-500'}`}
                    onClick={() => setViewMode('grid')}
                  >
                    <Grid3X3 className="w-3 h-3" />
                  </Button>
                </div>
              </div>

              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400" />
                  <Input
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    className="pl-7 bg-slate-50 border-slate-200 h-7 text-xs"
                  />
                </div>
              </div>

              {/* Category Pills - Smaller */}
              <ScrollArea className="pb-0.5">
                <div className="flex gap-1">
                  {categories.map(cat => (
                    <Button
                      key={cat}
                      variant={selectedCategory === cat ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setSelectedCategory(cat)}
                      className={`flex-shrink-0 h-5 text-xs px-2 ${
                        selectedCategory === cat 
                          ? 'bg-teal-500 text-white hover:bg-teal-600' 
                          : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      {cat === 'all' ? 'All' : cat}
                    </Button>
                  ))}
                </div>
              </ScrollArea>
            </div>

            {/* Products */}
            <ScrollArea className="flex-1 p-2">
              {filteredProducts.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-6 text-slate-400">
                  <Package className="w-8 h-8 mb-2 opacity-50" />
                  <p className="text-xs">No products found</p>
                  <Button variant="link" className="text-teal-600 text-xs mt-1" onClick={() => { setSearchQuery(''); setSelectedCategory('all'); }}>
                    Clear filters
                  </Button>
                </div>
              ) : viewMode === 'grid' ? (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-1.5">
                  {filteredProducts.map(p => (
                    <ProductCardGrid 
                      key={p.id} 
                      product={p} 
                      onSelect={handleProductSelect}
                      isSelected={selectedProduct?.id === p.id && !isCartItemSelected}
                    />
                  ))}
                </div>
              ) : (
                <div className="grid gap-1">
                  {filteredProducts.map(p => (
                    <ProductCardList 
                      key={p.id} 
                      product={p} 
                      onSelect={handleProductSelect}
                      isSelected={selectedProduct?.id === p.id && !isCartItemSelected}
                    />
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>
        </section>

        {/* Right Panel - Cart */}
        <aside className="hidden lg:flex flex-col w-72 border-l border-slate-200 bg-white flex-shrink-0">
          <div className="p-3 border-b border-slate-100 flex items-center justify-between">
            <h2 className="font-semibold flex items-center gap-2 text-slate-800 text-sm">
              <ShoppingCart className="w-4 h-4 text-teal-600" /> Your Caddy
            </h2>
            {itemCount > 0 && <Badge className="bg-teal-500 text-white text-xs">{itemCount}</Badge>}
          </div>
          <div className="flex-1 overflow-hidden">
            <CartPanel 
              cartItems={cartItems}
              subtotal={subtotal}
              tax={tax}
              total={total}
              offers={applicableOffers}
              onCheckout={handleCheckout}
              disabled={checkoutLoading}
              onItemClick={handleCartItemSelect}
            />
          </div>
        </aside>
      </main>

      {/* Mobile Bottom Bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-2 safe-area-pb z-30">
        <Button onClick={() => setShowCart(true)} className="w-full h-12 text-sm relative bg-gradient-to-r from-teal-500 to-cyan-500 text-white hover:from-teal-600 hover:to-cyan-600">
          <ShoppingCart className="w-4 h-4 mr-2" />
          View Caddy
          {itemCount > 0 && <Badge className="absolute -top-2 -right-2 bg-orange-500 text-white text-xs">{itemCount}</Badge>}
          <span className="ml-auto font-bold">₹{total.toFixed(2)}</span>
        </Button>
      </div>

      {/* Mobile Cart Sheet */}
      <Sheet open={showCart} onOpenChange={setShowCart}>
        <SheetContent side="bottom" className="h-[80vh] rounded-t-3xl bg-white border-slate-200">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2 text-slate-800">
              <ShoppingCart className="w-5 h-5 text-teal-600" /> Your Caddy ({itemCount})
            </SheetTitle>
          </SheetHeader>
          <div className="h-full overflow-hidden -mx-6">
            <CartPanel 
              cartItems={cartItems}
              subtotal={subtotal}
              tax={tax}
              total={total}
              offers={applicableOffers}
              onCheckout={handleCheckout}
              disabled={checkoutLoading}
              onItemClick={handleCartItemSelect}
            />
          </div>
        </SheetContent>
      </Sheet>

      {/* Checkout Dialog */}
      <Dialog open={showCheckout} onOpenChange={setShowCheckout}>
        <DialogContent className="sm:max-w-md bg-white border-slate-200">
          <DialogHeader>
            <DialogTitle className="text-slate-800">Confirm Payment</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-slate-50 rounded-xl p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Items ({itemCount})</span>
                <span className="text-slate-700">₹{subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Tax</span>
                <span className="text-slate-700">₹{tax.toFixed(2)}</span>
              </div>
              {applicableOffers.length > 0 && (
                <div className="flex justify-between text-sm text-teal-600">
                  <span>Offers Applied</span>
                  <span>{applicableOffers.length}</span>
                </div>
              )}
              <div className="flex justify-between font-bold text-lg pt-2 border-t border-slate-200">
                <span className="text-slate-800">Total</span>
                <span className="text-teal-600">₹{total.toFixed(2)}</span>
              </div>
            </div>

            <div className="bg-teal-50 border border-teal-200 rounded-xl p-4 text-teal-700 text-sm">
              <div className="flex items-center gap-2 font-medium">
                <CreditCard className="w-4 h-4" />
                Razorpay Secure Payment
              </div>
              <p className="mt-1 text-teal-600 text-xs">
                Pay securely using UPI, Cards, Net Banking, or Wallets
              </p>
            </div>
          </div>

          <DialogFooter className="flex flex-col gap-2 sm:flex-row sm:gap-2">
            <Button variant="outline" onClick={() => setShowCheckout(false)} disabled={checkoutLoading} className="flex-1 border-slate-200 text-slate-700 hover:bg-slate-50">
              Cancel
            </Button>
            <Button 
              data-testid="confirm-payment-btn"
              onClick={processPayment} 
              disabled={checkoutLoading} 
              className="flex-1 bg-teal-500 hover:bg-teal-600 text-white"
            >
              {checkoutLoading ? 'Processing...' : `Pay ₹${total.toFixed(2)}`}
            </Button>
          </DialogFooter>
          
          {/* Test mode option */}
          <div className="border-t border-slate-200 pt-3 mt-2">
            <Button 
              variant="ghost"
              onClick={processTestPayment} 
              disabled={checkoutLoading} 
              className="w-full text-slate-500 hover:text-slate-700 text-xs"
            >
              Skip Payment (Test Mode Only)
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Mobile bottom padding */}
      <div className="lg:hidden h-16" />
    </div>
  );
};

export default ShoppingDashboard;
